// ════════════════════════════════════════════════════════════════
// Preview Mode — Session-only store
// Stores a simulated PreviewContext for admin "view as" mode.
// ════════════════════════════════════════════════════════════════

import { createContext, useContext } from 'react';
import type { PreviewContext, AccessTier } from '@/lib/access-control/types';

export interface PreviewState {
  active: boolean;
  context: PreviewContext;
  /** Label shown in the banner */
  label: string;
  /** Timestamp when preview was activated */
  activatedAt: number | null;
}

export const EMPTY_PREVIEW: PreviewState = {
  active: false,
  context: {},
  label: '',
  activatedAt: null,
};

export type PreviewAction =
  | { type: 'activate'; context: PreviewContext; label: string }
  | { type: 'deactivate' };

export function previewReducer(state: PreviewState, action: PreviewAction): PreviewState {
  switch (action.type) {
    case 'activate':
      return {
        active: true,
        context: action.context,
        label: action.label,
        activatedAt: Date.now(),
      };
    case 'deactivate':
      return EMPTY_PREVIEW;
    default:
      return state;
  }
}

export interface PreviewContextValue {
  preview: PreviewState;
  activatePreview: (context: PreviewContext, label: string) => void;
  deactivatePreview: () => void;
}

export const PreviewCtx = createContext<PreviewContextValue>({
  preview: EMPTY_PREVIEW,
  activatePreview: () => {},
  deactivatePreview: () => {},
});

export function usePreview() {
  return useContext(PreviewCtx);
}
